/**
 * **数据就绪度（Data Readiness）闸门** —— 2026-10-05 第十七批。
 *
 * 用户原话（2026-10-05 确认口径）：
 *
 * > 跨平台视图默认 D-2 + 今天/昨天明确标注"仍在结算" + 每个视图显示"数据齐到哪天"。
 * > 改成这个方案，我明白仍然有偏差，但至少是 **N 个整天 对应 N 个整天**，
 * > 不会说时间多少都不一样，对吧！
 *
 * 本文件把「某个视图的数据**齐到哪天**」收敛成**零依赖纯函数**，供 `apps/api`
 * （只读端点 `/v1/data-readiness`）与 `apps/web`（默认区间 + 提示条 + 小字）共用。
 * 判定**不写在模板里、不写在 SQL 里**：否则同一份规则会有三份实现，迟早不一致。
 *
 * ## 一、就绪右端怎么来的（**不是写死的 D-1 / D-2 常量**）
 *
 * 每个平台贡献一个 `confirmedThrough`：
 *
 * ```
 * confirmedThrough(platform) = min( latestDataDate , expectedDataDate )
 * ```
 *
 *  · `expectedDataDate` = 「此刻按平台真实调度 + 数据滞后（T+N）**本应已产出**的最后
 *    一个业务日」——既有纯函数 `expectedLatestDataDate`（`sync-status.ts`）的产物，
 *    本文件**只消费、不重写**；
 *  · `latestDataDate` = 事实表里真实的最后一个业务日（未知 → `null`，**绝不折成 0**）。
 *
 * 视图的 `readyThroughDate` = 该视图**全部平台** `confirmedThrough` 的**最小值**。
 * 于是「跨平台为什么是 D-2」不需要一个常量来背：
 *
 * | 观测时刻（运维时区 HKT） | Meta（T+1，每天 00:40/06:40/12:40/18:40） | Google（T+1，每天 05:20） | 跨平台 min |
 * |---|---|---|---|
 * | 00:30 | 最近一次应运行 = 前一天 18:40 → 应到 **D-2** | 最近一次 = 前一天 05:20 → 应到 **D-2** | **D-2** |
 * | 05:00 | 00:40 已跑 → 应到 **D-1** | 05:20 **还没跑** → 应到 **D-2** | **D-2** |
 * | 10:00 | 06:40 已跑 → 应到 **D-1** | 05:20 已跑 → 应到 **D-1** | **D-1** |
 *
 * 也就是说：**「跨平台视图 = D-2」是"Google 当天 05:20 之前必然只有 D-2"的结论，
 * 不是一条常量**。05:20 之后两个平台都到 D-1，就绪右端自然前进到 D-1——这正是
 * 「按平台各自的调度 + 滞后推导」的意义。凌晨跑的时候，Meta 前一天的 T+1 事实
 * 也还没落库（`latestDataDate` 仍是 D-2），`min` 同样给出 D-2，两重保险。
 *
 * **纯 Meta 视图**（广告管理树 / 历史弹窗）只含 Meta 一个平台 → 就绪右端 =
 * `min(Meta.latest, Meta.expected)`：次日 01:00（00:40 运行完成后）前进到 D-1。
 *
 * ## 二、某平台停摆时**就绪右端不前进**
 *
 * `confirmedThrough` 取 `min(latest, expected)`：某平台连续几天没跑（或写失败）时
 * `latestDataDate` 停在旧日期，`min` 便把整个视图的右端**钉在旧日期上**，
 * 于是那些本该产出却没有的日子会被如实归入 `missingDates`（真实缺口），
 * **不会**被当成"平台节奏、等着就好"（`pendingDates`）。
 *
 * ⚠️ 与既有 `expectedLatestDataDate` 的分工：它**只被调度推导**、不被"上次成功运行"
 * 下推（否则停摆会被掩盖，见 `sync-status.ts` 的注释）。本文件用真实
 * `latestDataDate` 与它取 `min`，正是为了**不**掩盖停摆。
 *
 * ## 三、区间与「仍在结算」的判定
 *
 * 对选定区间 `[rangeStart, rangeEnd]` 里的**每一个业务日** `d`，按平台逐条判定：
 *
 *  1. 有任一平台 `d > expectedDataDate` → `d` **仍在结算**（`pendingDates`）：
 *     平台按自己的节奏还没轮到这一天，晚些时候会补上；
 *  2. 否则有任一平台 `latestDataDate === null` 或 `d > latestDataDate`
 *     → `d` 是**真实缺口**（`missingDates`）：按调度早该有了，实际没有；
 *  3. 否则 `d` 已就绪（`readyDates`）。
 *
 * 与第 1 条的算法自洽：`d <= readyThroughDate` ⟺ 对每个平台 `d <= min(latest, expected)`
 * ⟺ `d` 已就绪。因此 `pendingDates ∪ missingDates` 恰好是区间里晚于就绪右端的日子。
 *
 * `settled` = 「区间右端 `rangeEnd` 是否已结算」= `rangeEnd <= readyThroughDate`。
 * `readyThroughDate === null`（无法推导）时 `settled` 一律 `false`——**未知不等于已就绪**。
 *
 * ## 四、口径纪律
 *
 *  · 金额 / 比率一概不碰（本文件只处理业务日）；
 *  · `null` = 未知，**绝不**折成某个哨兵日期、也绝不折成 `0`；
 *  · 没有任何平台数据时 `readyThroughDate = null`，用中文如实说明原因；
 *  · 平台**未接入 / 读失败**（`latestDataDate === null`）单独列入
 *    `unavailablePlatforms` 并出现在文案里，**不会**被当成"数据已齐"。
 */

import { recentBusinessDates, shiftIsoDate } from "./ad-tree-history.js";
import type { IsoDate } from "./metrics.js";
import {
  businessDateInTimezone,
  expectedLatestDataDate,
  SYNC_PLATFORM_LABELS,
  type SyncPlatform,
} from "./sync-status.js";

// ---------------------------------------------------------------------------
// 视图分类
// ---------------------------------------------------------------------------

/**
 * 视图分类（**由「这个视图的指标来自哪些平台」决定**，不是由页面名字决定）。
 *
 *  · `cross_platform`：跨平台视图（概览 / MER / 对账 / 花费总览）。指标的分母同时
 *    依赖 Meta 与 Google（以及 Shopify 收入），因此就绪右端 = **全部平台**的 min；
 *  · `meta_only`：纯 Meta 视图（广告管理树 / 历史弹窗 / 广告指标表）。就绪右端只看 Meta。
 */
export type ReadinessViewKind = "cross_platform" | "meta_only";

export interface ReadinessViewDefinition {
  readonly kind: ReadinessViewKind;
  /** 中文短名（提示条 / 调试用）。 */
  readonly label: string;
  /** 该视图的指标**必须**覆盖的平台（就绪右端取这些平台的最小值）。 */
  readonly platforms: readonly SyncPlatform[];
  /** 该视图「最近 N 天」的默认档（跨平台概览 30 天；纯 Meta 广告管理 7 天）。 */
  readonly defaultWindowDays: number;
}

/**
 * 视图定义表（**唯一真相源**）。加一个新视图时只改这里，别在页面里另写一份平台清单。
 */
export const READINESS_VIEW_DEFINITIONS: Readonly<Record<ReadinessViewKind, ReadinessViewDefinition>> = {
  cross_platform: {
    kind: "cross_platform",
    label: "跨平台视图",
    // 顺序 = 页面展示顺序（与 `SYNC_PLATFORMS` 一致）。
    platforms: ["meta", "google", "shopify"],
    defaultWindowDays: 30,
  },
  meta_only: {
    kind: "meta_only",
    label: "纯 Meta 视图",
    platforms: ["meta"],
    defaultWindowDays: 7,
  },
};

/**
 * 「该平台的数据大概几点齐」——**仅供文案展示**（提示条里写"Meta 01:00 齐"）。
 *
 * ⚠️ 就绪右端的**推导不使用本表**（它由 `expectedLatestDataDate` 的调度 + T+N 推导）。
 * 这里是生产实测的结算时刻（来源见 `SYNC_PLATFORM_SETTLE_SOURCE`），与
 * `SYNC_PLATFORM_SCHEDULES` 的 timer 是同一条事实的两种表达，改 timer 时一并复核。
 */
export const SYNC_PLATFORM_SETTLE_CLOCK: Readonly<Record<SyncPlatform, string>> = {
  meta: "01:00",
  google: "05:30",
  shopify: "持续写入",
};

/** `SYNC_PLATFORM_SETTLE_CLOCK` 的来源（写进页面 tooltip / 报告，避免"某处拍脑袋"）。 */
export const SYNC_PLATFORM_SETTLE_SOURCE =
  "生产实测（2026-10-05）：Meta insights 每天 00:40 起跑、T+1，昨天数据次日约 01:00 齐；" +
  "Google 每天 05:20 起跑、T+1，昨天数据次日约 05:30 齐；Shopify 订单每小时 :20、T+0，当天持续写入。";

// ---------------------------------------------------------------------------
// 输入 / 输出
// ---------------------------------------------------------------------------

/** 单个平台的就绪观测量（`expectedDataDate` 由 `expectedLatestDataDate` 预先算出）。 */
export interface PlatformReadinessObservation {
  readonly platform: SyncPlatform;
  /** 事实表里真实的最后一个业务日；`null` = 该平台没有任何数据（未接入 / 读失败）。 */
  readonly latestDataDate: IsoDate | null;
  /** 此刻按调度 + 滞后本应已产出的最后一个业务日；`null` = 无法推导。 */
  readonly expectedDataDate: IsoDate | null;
}

export interface DataReadinessInput {
  readonly viewKind: ReadinessViewKind;
  /** 观测时刻（ISO-8601，由路由的 `generatedAt` 注入；测试可复现）。 */
  readonly nowIso: string;
  /** 运维时区（与 systemd timer 的服务器时区一致）。 */
  readonly opsTimeZone: string;
  readonly platforms: readonly PlatformReadinessObservation[];
  /** 选定区间左端（可空 → 只给就绪右端，不算区间明细）。 */
  readonly rangeStart?: IsoDate | null;
  /** 选定区间右端（可空 → 同上；`settled` 需要它）。 */
  readonly rangeEnd?: IsoDate | null;
}

export interface DataReadiness {
  readonly viewKind: ReadinessViewKind;
  readonly viewLabel: string;
  /**
   * **该视图「数据齐到哪天」**：全部相关平台 `min(latest, expected)` 的最小值。
   * `null` = 无法确认（没有任何平台能同时给出真实数据日与预期产出日）。
   */
  readonly readyThroughDate: IsoDate | null;
  /** 选定区间的右端是否已结算（`rangeEnd <= readyThroughDate`）。未知 → `false`。 */
  readonly settled: boolean;
  /** 观测时刻在运维时区的「今天」（`null` = 时刻 / 时区非法，不折成任何日期）。 */
  readonly opsToday: IsoDate | null;
  /** 区间内**仍在结算**的日子（平台按节奏还没轮到），升序。 */
  readonly pendingDates: readonly IsoDate[];
  /** 区间内**已过预期产出时间却没有数据**的日子（真实缺口），升序。 */
  readonly missingDates: readonly IsoDate[];
  /** 区间内三个平台都已有数据（且已到预期产出时间）的日子，升序。 */
  readonly readyDates: readonly IsoDate[];
  /** 把就绪右端钉住的平台（`confirmedThrough === readyThroughDate`）。 */
  readonly limitingPlatforms: readonly SyncPlatform[];
  /** 选定区间右端**还没轮到**的平台（`expectedDataDate < rangeEnd`）。 */
  readonly pendingPlatforms: readonly SyncPlatform[];
  /** 真实落后于自己调度节奏的平台（`latestDataDate < expectedDataDate`）。 */
  readonly stalledPlatforms: readonly SyncPlatform[];
  /** 完全没有数据的平台（未接入 / 读取失败）——**不是**"数据已齐"。 */
  readonly unavailablePlatforms: readonly SyncPlatform[];
  /** 无法推导预期产出日的平台（调度不可解析 / 没有运行记录）。 */
  readonly underivablePlatforms: readonly SyncPlatform[];
  /** 一句话说明（UI 直接显示；日期用 `MM-DD`，整年见 `pendingDates` / `missingDates`）。 */
  readonly summary: string;
  /** 每平台一行复核文本（含真实值 / 预期值 / 是否落后），供 tooltip 与排障。 */
  readonly platformNotes: readonly string[];
}

interface PlatformFacts {
  readonly platform: SyncPlatform;
  readonly label: string;
  readonly latest: IsoDate | null;
  readonly expected: IsoDate | null;
  /** `min(latest, expected)`；任一为 `null` → `null`（无法确认）。 */
  readonly confirmed: IsoDate | null;
  /** 真实数据落后于自己的调度预期（停摆 / 写入失败）。 */
  readonly stalled: boolean;
  /** 该平台没有任何数据。 */
  readonly unavailable: boolean;
}

/** `YYYY-MM-DD` 形状（不校验日历真实性——输入来自事实表 / 纯函数，形状即可）。 */
function isoShape(value: string | null | undefined): value is IsoDate {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * 两个业务日取较小者；`null` = 未知，**不参与比较**。
 *
 * 注意：调用方只在「所有相关平台的 `confirmed` 都非 `null`」时才用它算
 * `readyThroughDate`（见下面的严格门）——因此这里的 `null` 分支只是防御性的，
 * **不会**把"未知"当成"最小值"。
 */
function minIsoDate(left: IsoDate | null, right: IsoDate | null): IsoDate | null {
  if (left === null) return right;
  if (right === null) return left;
  return left <= right ? left : right;
}

/** `MM-DD`（UI 列表用；跨年时 `pendingDates` 里仍是完整 ISO 日期）。 */
function formatDayList(dates: readonly IsoDate[]): string {
  return dates.map((date) => date.slice(5)).join("、");
}

function labelList(platforms: readonly SyncPlatform[]): string {
  return platforms.map((platform) => SYNC_PLATFORM_LABELS[platform]).join("、");
}

/**
 * **就绪度判定的唯一实现**（规则见文件头）。纯函数：同样的输入永远同样的输出。
 */
export function dataReadiness(input: DataReadinessInput): DataReadiness {
  const definition = READINESS_VIEW_DEFINITIONS[input.viewKind];
  const byPlatform = new Map<SyncPlatform, PlatformReadinessObservation>();
  for (const row of input.platforms) byPlatform.set(row.platform, row);

  const facts: PlatformFacts[] = definition.platforms.map((platform) => {
    const row = byPlatform.get(platform);
    const latest = row !== undefined && isoShape(row.latestDataDate) ? row.latestDataDate : null;
    const expected = row !== undefined && isoShape(row.expectedDataDate) ? row.expectedDataDate : null;
    const confirmed = latest === null || expected === null ? null : minIsoDate(latest, expected);
    return {
      platform,
      label: SYNC_PLATFORM_LABELS[platform],
      latest,
      expected,
      confirmed,
      stalled: latest !== null && expected !== null && latest < expected,
      unavailable: latest === null,
    };
  });

  const unavailablePlatforms = facts.filter((fact) => fact.unavailable).map((fact) => fact.platform);
  const underivablePlatforms = facts
    .filter((fact) => !fact.unavailable && fact.expected === null)
    .map((fact) => fact.platform);

  // ⚠️ 只要视图依赖的**任一平台**「没有数据」或「预期产出日无法推导」，就不能认证
  // 这个视图"数据齐到哪天"——返回 `null`（未知），而不是拿其余平台的最小值冒充完整。
  // 这样才能满足「NULL / 未知绝不折 0」：一个缺 Google 的跨平台视图不是一个完整的视图。
  let readyThroughDate: IsoDate | null = null;
  if (unavailablePlatforms.length === 0 && underivablePlatforms.length === 0) {
    for (const fact of facts) readyThroughDate = minIsoDate(readyThroughDate, fact.confirmed);
  }

  const limitingPlatforms = facts
    .filter((fact) => fact.confirmed !== null && fact.confirmed === readyThroughDate)
    .map((fact) => fact.platform);
  const stalledPlatforms = facts.filter((fact) => fact.stalled).map((fact) => fact.platform);

  const rangeStart = isoShape(input.rangeStart) ? input.rangeStart : null;
  const rangeEnd = isoShape(input.rangeEnd) ? input.rangeEnd : null;
  // 区间明细只在「两端都有且不倒置」时计算；否则留空（不猜一个区间出来）。
  let dates: readonly IsoDate[] = [];
  if (rangeStart !== null && rangeEnd !== null && rangeStart <= rangeEnd) {
    dates = recentBusinessDates(rangeEnd, dayCount(rangeStart, rangeEnd));
  }

  const pendingDates: IsoDate[] = [];
  const missingDates: IsoDate[] = [];
  const readyDates: IsoDate[] = [];
  for (const date of dates) {
    let pending = false;
    let missing = false;
    for (const fact of facts) {
      if (fact.expected !== null && date > fact.expected) {
        // 该平台按节奏还没轮到这一天：仍在结算（晚些时候会补上）。
        pending = true;
        continue;
      }
      // 到这里说明 `d <= expectedDate`（该平台**本应已有**这一天）：缺了就是真缺口。
      // `expected === null`（无法推导）同样计入缺口——未知不等于已就绪。
      if (fact.latest === null || fact.expected === null || date > fact.latest) missing = true;
    }
    // ⚠️ **真实缺口优先于"仍在结算"**：同一天可能对 A 平台是真实缺口、对 B 平台还没轮到，
    // 此时必须报缺口（否则停摆平台会被"另一个平台还没跑"掩盖成无害的 pending）。
    if (missing) missingDates.push(date);
    else if (pending) pendingDates.push(date);
    else readyDates.push(date);
  }

  const pendingPlatforms =
    rangeEnd === null
      ? []
      : facts
          .filter((fact) => fact.expected !== null && fact.expected < rangeEnd)
          .map((fact) => fact.platform);

  const settled = rangeEnd !== null && readyThroughDate !== null && rangeEnd <= readyThroughDate;

  const summary = buildReadinessSummary({
    readyThroughDate,
    pendingDates,
    missingDates,
    stalledPlatforms,
    unavailablePlatforms,
    underivablePlatforms,
  });

  const platformNotes = facts.map((fact) => {
    const staleness = fact.stalled ? "（实际落后于调度：可能有同步停摆）" : "";
    return (
      `${fact.label}：最新数据 ${fact.latest ?? "无数据"}；` +
      `按调度此刻应到 ${fact.expected ?? "无法推导"}${staleness}`
    );
  });

  return {
    viewKind: input.viewKind,
    viewLabel: definition.label,
    readyThroughDate,
    settled,
    opsToday: readinessOpsToday(input.nowIso, input.opsTimeZone),
    pendingDates,
    missingDates,
    readyDates,
    limitingPlatforms,
    pendingPlatforms,
    stalledPlatforms,
    unavailablePlatforms,
    underivablePlatforms,
    summary,
    platformNotes,
  };
}

/** 区间天数（含两端）；非法 / 倒序 → 1（调用方已保证 `bounded`）。 */
function dayCount(from: IsoDate, to: IsoDate): number {
  const fromMs = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const toMs = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.round((toMs - fromMs) / 86_400_000) + 1;
}

/** 一句话说明（用户要求：「数据齐到 2026-10-03；10-04、10-05 仍在结算」）。 */
function buildReadinessSummary(input: {
  readonly readyThroughDate: IsoDate | null;
  readonly pendingDates: readonly IsoDate[];
  readonly missingDates: readonly IsoDate[];
  readonly stalledPlatforms: readonly SyncPlatform[];
  readonly unavailablePlatforms: readonly SyncPlatform[];
  readonly underivablePlatforms: readonly SyncPlatform[];
}): string {
  if (input.readyThroughDate === null) {
    const why: string[] = [];
    if (input.unavailablePlatforms.length > 0) {
      why.push(`${labelList(input.unavailablePlatforms)} 没有任何数据`);
    }
    if (input.underivablePlatforms.length > 0) {
      why.push(`${labelList(input.underivablePlatforms)} 的预期产出时间无法推导`);
    }
    return `无法确认数据齐到哪天（${why.length > 0 ? why.join("；") : "缺少平台状态"}）`;
  }
  let text = `数据齐到 ${input.readyThroughDate}`;
  if (input.pendingDates.length > 0) text += `；${formatDayList(input.pendingDates)} 仍在结算`;
  if (input.missingDates.length > 0) text += `；${formatDayList(input.missingDates)} 已过产出时间但没有数据`;
  if (input.stalledPlatforms.length > 0) {
    text += `；${labelList(input.stalledPlatforms)} 的数据落后于自己的产出节奏`;
  }
  if (input.unavailablePlatforms.length > 0) {
    text += `；${labelList(input.unavailablePlatforms)} 无任何数据`;
  }
  return text;
}

// ---------------------------------------------------------------------------
// 与「平台同步状态」的接线（复用既有 `expectedLatestDataDate`，不另写一套）
// ---------------------------------------------------------------------------

/** 从平台同步状态抽出的最小输入（`latestDataDate` + 运行台账）。 */
export interface PlatformScheduleObservation {
  readonly platform: SyncPlatform;
  readonly latestDataDate: IsoDate | null;
  /** 最近一次已结束运行（`expectedLatestDataDate` 只在调度不可解析时用它兜底）。 */
  readonly lastRunAtIso?: string | null;
}

/**
 * 由**调度 + 滞后**推导每个平台的预期产出日（逐字复用 `expectedLatestDataDate`）。
 *
 * ⚠️ 不在这里复制任何 T+N / OnCalendar 规则：`expectedLatestDataDate` 是唯一实现。
 */
export function derivePlatformReadiness(input: {
  readonly platforms: readonly PlatformScheduleObservation[];
  readonly nowIso: string;
  readonly opsTimeZone: string;
}): readonly DerivedPlatformReadiness[] {
  return input.platforms.map((row) => {
    const expected = expectedLatestDataDate({
      platform: row.platform,
      nowIso: input.nowIso,
      opsTimeZone: input.opsTimeZone,
      ...(row.lastRunAtIso === undefined || row.lastRunAtIso === null
        ? {}
        : { lastRunAtIso: row.lastRunAtIso }),
    });
    return {
      platform: row.platform,
      latestDataDate: row.latestDataDate,
      expectedDataDate: expected.date,
      expectedDataDateReason: expected.reason,
    };
  });
}

/** `derivePlatformReadiness` 的结果：观测量 + 预期产出日的中文依据（可复核）。 */
export interface DerivedPlatformReadiness extends PlatformReadinessObservation {
  readonly expectedDataDateReason: string;
}

/** 观测时刻在运维时区的「今天」（`null` = 时区/时刻非法，**不折成任何日期**）。 */
export function readinessOpsToday(nowIso: string, opsTimeZone: string): IsoDate | null {
  return businessDateInTimezone(nowIso, opsTimeZone);
}

// ---------------------------------------------------------------------------
// 只读端点 `/v1/data-readiness` 的响应形状（api 组装 / web 消费）
// ---------------------------------------------------------------------------

/**
 * 就绪度端点使用的运维时区。
 *
 * ⚠️ 必须与 `@ams/store` 的 `DEFAULT_SYNC_OPS_TIMEZONE`（= systemd timer 的服务器时区）
 * 同值：端点**始终**用 `derivePlatformReadiness` 自算预期产出日（不依赖端口是否回填
 * `expectedDataThroughDate`），两边只有用同一时区才会得出同一天。
 */
export const DEFAULT_READINESS_OPS_TIMEZONE = "Asia/Hong_Kong";

/** 一个平台的就绪观测量（含展示字段；`expectedDataDate` 为 `null` = 无法推导）。 */
export interface DataReadinessPlatform {
  readonly platform: SyncPlatform;
  readonly label: string;
  readonly latestDataDate: IsoDate | null;
  readonly expectedDataDate: IsoDate | null;
  /** `expectedDataDate` 的推导依据（中文一句话，可显示 / 可复核）。 */
  readonly expectedDataDateReason: string;
  /** 该平台最近一次已结束运行（`null` = 该平台不写运行台账 / 从未运行）。 */
  readonly lastRunAtIso: string | null;
  readonly lastRunSource: string;
  /** 同步频率展示文本（如「每天 4 次（00:40 / 06:40 / 12:40 / 18:40）」）。 */
  readonly frequencyDisplay: string;
  /** 该平台数据"大概几点齐"（**展示用**，见 `SYNC_PLATFORM_SETTLE_CLOCK`）。 */
  readonly settleClock: string;
}

/**
 * `GET /v1/data-readiness` 的 `data`。
 *
 * ⚠️ 不含区间：区间由调用方（web）在本地用同一个纯函数 `dataReadiness` 代入
 * `rangeStart` / `rangeEnd` 现算——端点因此保持「轻」（一个请求服务所有视图与任意区间），
 * 而**判定只有一份实现**。
 */
export interface DataReadinessData {
  /** 观测时刻（ISO-8601），web 回传给 `dataReadiness` 的 `nowIso`。 */
  readonly asOf: string;
  readonly opsTimeZone: string;
  readonly settleClockSource: string;
  readonly platforms: readonly DataReadinessPlatform[];
  /** 不带区间时两个视图的就绪度（`readyThroughDate` / 说明；区间明细为空数组）。 */
  readonly views: Readonly<Record<ReadinessViewKind, DataReadiness>>;
}

// ---------------------------------------------------------------------------
// 默认区间（「到就绪右端为止的 N 天」）
// ---------------------------------------------------------------------------

/**
 * `[end - (days - 1), end]`（含两端，共 `days` 天）；`days < 1` / 非法日期 → `null`。
 *
 * 这是「最近 N 天 = N 个整天」的**唯一区间算法**：
 *  · 参考右端 = **昨天**时，它逐字等于既有的「不含今天」口径 `[今天-N, 今天-1]`；
 *  · 参考右端 = **就绪右端**时，它给出「到数据齐到那天为止的 N 个整天」。
 *
 * 因此「不含今天」与「就绪度默认区间」**不是两套规则**：同一公式、只换参考右端
 * （见 `apps/web/src/lib/dateRange.ts` 的 `referenceEnd`）。
 */
export function rangeEndingOn(endDate: IsoDate, days: number): { readonly from: IsoDate; readonly to: IsoDate } | null {
  if (!isoShape(endDate)) return null;
  if (!Number.isFinite(days) || Math.trunc(days) !== days || days < 1) return null;
  // 借 `shiftIsoDate` 的序数算术做月 / 年 / 闰年跨界（不在这里另写一份日历算术）。
  const shifted = shiftIsoDate(endDate, -(days - 1));
  if (!isoShape(shifted)) return null;
  return { from: shifted, to: endDate };
}
